// Builds the monthly report workbooks in the same layout as the client's own files
// ("Amazon Combined Summary", "Shopify ... Working"): raw lines -> Working -> Summary -> Order ->
// Refund -> COGS -> Supplier COGS (COGS + freight with product sales) -> Profit -> Check.
import * as XLSX from 'xlsx';

const MONEY = ['product_sales', 'product_sales_tax', 'postage_credits', 'shipping_credits_tax', 'gift_wrap_credits', 'giftwrap_credits_tax',
  'promotional_rebates', 'promotional_rebates_tax', 'marketplace_withheld_tax', 'selling_fees', 'fba_fees', 'other_transaction_fees', 'other', 'total'];
const MONEY_HEAD = ['product sales', 'product sales tax', 'postage credits', 'shipping credits tax', 'gift wrap credits', 'giftwrap credits tax',
  'promotional rebates', 'promotional rebates tax', 'marketplace withheld tax', 'selling fees', 'fba fees', 'other transaction fees', 'other', 'total'];

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const col = (i) => XLSX.utils.encode_col(i); // 0 -> A
const hasRate = (r) => r.rate !== null && r.rate !== undefined && (Number(r.rate) > 0 || Number(r.freight) > 0);

// group rows by the key fields and add up the sum fields
function group(rows, keys, sums) {
  const map = new Map();
  for (const r of rows) {
    const k = keys.map(x => r[x]).join('\u0001');
    if (!map.has(k)) map.set(k, { ...Object.fromEntries(keys.map(x => [x, r[x]])), ...Object.fromEntries(sums.map(s => [s, 0])), _first: r });
    const g = map.get(k);
    for (const s of sums) g[s] += Number(r[s]) || 0;
  }
  return [...map.values()].map(g => { for (const s of sums) g[s] = r2(g[s]); return g; });
}

function sheet(aoa, widths) {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  if (widths) ws['!cols'] = widths.map(w => ({ wch: w }));
  return ws;
}

// ───────────────────────────── Amazon ─────────────────────────────
export function buildAmazonWorkbook(data) {
  const wb = XLSX.utils.book_new();
  const rows = data.rows.map(r => ({ ...r, label: r.label || '-' }));
  const stores = [...new Set(rows.map(r => r.store))].sort();

  // 1) one tab per store with every transaction line (like the client's GRS / Ibex tabs)
  for (const s of stores) {
    const head = ['Store', 'date/time', 'Day', 'Month', 'Year', 'type', 'order id', 'sku', 'Article', 'description', 'quantity', 'marketplace',
      'fulfilment', ...MONEY_HEAD, 'Transaction status'];
    const aoa = [head];
    for (const r of rows.filter(x => x.store === s)) {
      const d = r.posted_at ? new Date(r.posted_at) : null;
      aoa.push([r.store, d ? d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '', d ? d.getUTCDate() : '', d ? d.getUTCMonth() + 1 : '', d ? d.getUTCFullYear() : '',
        r.type, r.order_id || '', r.sku || '', r.label, r.description || '', r.quantity, r.marketplace || '', r.fulfilment,
        ...MONEY.map(m => r2(r[m])), r.status || '']);
    }
    XLSX.utils.book_append_sheet(wb, sheet(aoa, [8, 24, 5, 6, 6, 22, 22, 22, 12, 50, 8, 14, 10, ...MONEY.map(() => 12), 12]), s.slice(0, 31));
  }

  // 2) Working: one line per transaction, the fields the summary uses
  XLSX.utils.book_append_sheet(wb, sheet([['Store', 'type', 'fulfilment', 'Article', 'quantity', 'product sales'],
    ...rows.map(r => [r.store, r.type, r.fulfilment, r.label, r.quantity, r2(r.product_sales)])], [8, 30, 10, 14, 9, 13]), 'Working');

  // 3) Summary / Order / Refund
  const summary = group(rows, ['store', 'type', 'fulfilment', 'label'], ['quantity', 'product_sales'])
    .sort((a, b) => a.store.localeCompare(b.store) || a.type.localeCompare(b.type) || a.fulfilment.localeCompare(b.fulfilment) || String(a.label).localeCompare(String(b.label)));
  const sumHead = ['Store', 'type', 'fulfilment', 'Article', 'quantity', 'product sales'];
  const sumRow = (g) => [g.store, g.type, g.fulfilment, g.label, g.quantity, g.product_sales];
  XLSX.utils.book_append_sheet(wb, sheet([sumHead, ...summary.map(sumRow)], [8, 40, 10, 16, 9, 13]), 'Summary');
  const orders = summary.filter(g => g.type === 'Order');
  XLSX.utils.book_append_sheet(wb, sheet([sumHead, ...orders.map(sumRow)], [8, 10, 10, 16, 9, 13]), 'Order');

  const refunds = summary.filter(g => g.type === 'Refund');
  const ref = [[...sumHead, null, null, 'Seller', 'Amazon']];
  const n = Math.max(refunds.length, 1) + 1;
  for (let i = 0; i < Math.max(refunds.length, stores.length); i++) {
    const row = refunds[i] ? sumRow(refunds[i]) : [null, null, null, null, null, null];
    if (i < stores.length) {
      const st = stores[i];
      row.push(null, st, { f: `-SUMIFS($F$2:$F${n},$A$2:$A${n},"${st}",$C$2:$C${n},"Seller")` }, { f: `-SUMIFS($F$2:$F${n},$A$2:$A${n},"${st}",$C$2:$C${n},"Amazon")` });
    }
    ref.push(row);
  }
  XLSX.utils.book_append_sheet(wb, sheet(ref, [8, 10, 10, 16, 9, 13, 3, 8, 12, 12]), 'Refund');

  // 4) COGS and supplier sheets: order lines per Store / Fulfilment / Article / Style
  const cogsLines = group(rows.filter(r => r.type === 'Order'), ['store', 'fulfilment', 'article', 'style'], ['quantity', 'product_sales'])
    .map(g => ({ ...g, rate: g._first.rate, freight: g._first.freight }))
    .sort((a, b) => a.store.localeCompare(b.store) || a.article.localeCompare(b.article) || a.style.localeCompare(b.style));
  const sections = [
    ['Fulfilled by Amazon', cogsLines.filter(l => l.fulfilment === 'Amazon' && hasRate(l))],
    ['Fulfilled by Seller', cogsLines.filter(l => l.fulfilment !== 'Amazon' && hasRate(l))],
    ['No cost rate (not supplied / rate missing)', cogsLines.filter(l => !hasRate(l))],
  ];
  XLSX.utils.book_append_sheet(wb, cogsSheet(sections, false, 'amazon'), 'COGS');
  XLSX.utils.book_append_sheet(wb, cogsSheet(sections, true, 'amazon'), 'Supplier COGS');

  // 5) Profit per store
  XLSX.utils.book_append_sheet(wb, amazonProfit(rows, cogsLines, stores), 'Profit');
  XLSX.utils.book_append_sheet(wb, checkSheet(rows), 'Check');
  return wb;
}

// COGS-style sheet. Amazon: Store, Type, Fulfilment, Article, Style, Quantity, [Product Sales], Rate, Freight, COGS, Total Freight
function cogsSheet(sections, withSales, platform) {
  const aoa = [];
  for (const [title, lines] of sections) {
    if (!lines.length) continue;
    if (aoa.length) aoa.push([], []);
    aoa.push([title]);
    const head = platform === 'amazon'
      ? ['Store', 'Type', 'Fulfilment', 'Article', 'Style', 'Quantity', ...(withSales ? ['Product Sales'] : []), 'Rate', 'Freight', 'COGS', 'Total Freight']
      : ['Store', 'Source', 'Financial Status', 'Article', 'Style', 'Lineitem price', 'Lineitem quantity', ...(withSales ? ['Subtotal', 'Total'] : []), 'Rate', 'Freight', 'COGS', 'Total Freight'];
    aoa.push(head);
    const start = aoa.length + 1;
    const qi = head.indexOf(platform === 'amazon' ? 'Quantity' : 'Lineitem quantity');
    const ri = head.indexOf('Rate'), fi = head.indexOf('Freight'), ci = head.indexOf('COGS'), ti = head.indexOf('Total Freight');
    lines.forEach((l, i) => {
      const r = start + i;
      const row = platform === 'amazon'
        ? [l.store, 'Order', l.fulfilment, l.article, l.style, l.quantity, ...(withSales ? [l.product_sales] : []), Number(l.rate) || 0, Number(l.freight) || 0]
        : [l.store, l.source, l.financial_status, l.article, l.style, l.price, l.quantity, ...(withSales ? [l.subtotal, l.total] : []), Number(l.rate) || 0, Number(l.freight) || 0];
      row.push({ f: `${col(qi)}${r}*${col(ri)}${r}` }, { f: `${col(qi)}${r}*${col(fi)}${r}` });
      if (withSales) {
        const next = lines[i + 1];
        if (!next || next.store !== l.store) {
          const first = start + lines.findIndex(x => x.store === l.store);
          row.push({ f: `SUM(${col(ti)}${first}:${col(ti)}${r})` });
        }
      }
      aoa.push(row);
    });
    const end = start + lines.length - 1;
    const total = head.map(() => null);
    total[0] = 'Total';
    const sumCols = [qi, ci, ti, ...(withSales ? (platform === 'amazon' ? [head.indexOf('Product Sales')] : [head.indexOf('Subtotal'), head.indexOf('Total')]) : [])];
    for (const c of sumCols) total[c] = { f: `SUM(${col(c)}${start}:${col(c)}${end})` };
    aoa.push(total);
  }
  if (withSales) aoa.push([], ['Column L: freight subtotal for each store (at the store\'s last line).']);
  return sheet(aoa.length ? aoa : [['No order lines this month']], platform === 'amazon' ? [8, 8, 11, 20, 14, 9, 13, 7, 8, 11, 12, 12] : [9, 10, 15, 20, 14, 13, 16, 11, 11, 7, 8, 11, 12, 12]);
}

function amazonProfit(rows, cogsLines, stores) {
  const aoa = [['Profit by store', ...stores, 'All stores'], []];
  const sum = (s, f, pick) => r2(rows.filter(r => (s === '*' || r.store === s) && pick(r)).reduce((t, r) => t + (Number(r[f]) || 0), 0));
  const cogs = (s, k) => r2(cogsLines.filter(l => s === '*' || l.store === s).reduce((t, l) => t + l.quantity * (Number(k === 'cogs' ? l.rate : l.freight) || 0), 0));
  const line = (label, fn) => aoa.push([label, ...stores.map(fn), fn('*')]);
  const notTransfer = (r) => r.type !== 'Transfer';
  line('Order product sales', s => sum(s, 'product_sales', r => r.type === 'Order'));
  line('Refunds (product sales)', s => sum(s, 'product_sales', r => r.type === 'Refund'));
  line('Selling fees', s => sum(s, 'selling_fees', notTransfer));
  line('FBA fees', s => sum(s, 'fba_fees', notTransfer));
  line('Other transaction fees', s => sum(s, 'other_transaction_fees', notTransfer));
  line('Net from Amazon (all transactions except payouts)', s => sum(s, 'total', notTransfer));
  aoa.push([]);
  line('COGS (orders x rate)', s => -cogs(s, 'cogs'));
  line('Freight (orders x freight)', s => -cogs(s, 'freight'));
  aoa.push([]);
  const netRow = aoa.findIndex(r => r[0] && String(r[0]).startsWith('Net from Amazon')) + 1;
  const cogsRow = aoa.findIndex(r => r[0] === 'COGS (orders x rate)') + 1;
  aoa.push(['Profit (net - COGS - freight)', ...[...stores, '*'].map((_, i) => ({ f: `${col(i + 1)}${netRow}+${col(i + 1)}${cogsRow}+${col(i + 1)}${cogsRow + 1}` }))]);
  aoa.push([], ['Payouts to the bank (Transfer) are not counted as income or cost.', ...stores.map(s => sum(s, 'total', r => r.type === 'Transfer'))]);
  return sheet(aoa, [52, ...stores.map(() => 14), 14]);
}

function checkSheet(rows) {
  const noLabel = group(rows.filter(r => r.sku && r.label === '-'), ['store', 'sku'], ['quantity']);
  const noRate = group(rows.filter(r => r.type === 'Order' && r.label !== '-' && !hasRate(r)), ['article', 'style'], ['quantity']);
  const aoa = [['Things to check in this report'], [],
    ['SKUs with no Article (not in your previous reports or the SKU mapping sheet)'], ['Store', 'SKU', 'Quantity'],
    ...noLabel.map(g => [g.store, g.sku, g.quantity]), [],
    ['Article / Style with no cost rate (add it on the COGS Rates page)'], ['Article', 'Style', 'Quantity sold'],
    ...noRate.map(g => [g.article, g.style, g.quantity])];
  return sheet(aoa, [60, 24, 12]);
}

// ───────────────────────────── Shopify ─────────────────────────────
export function buildShopifyWorkbook(data) {
  const wb = XLSX.utils.book_new();
  const rows = data.rows;
  XLSX.utils.book_append_sheet(wb, sheet([['Store', 'Order', 'Created at', 'Source', 'Financial Status', 'Lineitem sku', 'Lineitem name', 'Article',
    'Lineitem price', 'Lineitem quantity', 'Subtotal', 'Total', 'Refunded'],
    ...rows.map(r => [r.store, r.order, String(r.order_date).replace('T', ' ').slice(0, 19), r.source, r.financial_status, r.sku || '', r.title || '', r.label,
      r.price, r.quantity, r2(r.subtotal), r2(r.total), r2(r.refunded)])], [8, 9, 20, 10, 15, 22, 50, 12, 12, 16, 10, 10, 10]), 'Orders');
  const workHead = ['Store', 'Source', 'Financial Status', 'Article', 'Lineitem price', 'Lineitem quantity', 'Subtotal', 'Total'];
  XLSX.utils.book_append_sheet(wb, sheet([workHead, ...rows.map(r => [r.store, r.source, r.financial_status, r.label, r.price, r.quantity, r2(r.subtotal), r2(r.total)])],
    [8, 10, 15, 12, 13, 16, 10, 10]), 'Working');
  const summary = group(rows, ['store', 'source', 'financial_status', 'label', 'price'], ['quantity', 'subtotal', 'total'])
    .sort((a, b) => String(a.label).localeCompare(String(b.label)) || String(a.source).localeCompare(String(b.source)) || a.price - b.price);
  const sumRow = (g) => [g.store, g.source, g.financial_status, g.label, g.price, g.quantity, g.subtotal, g.total];
  const isRefund = (s) => /refund/.test(s);
  XLSX.utils.book_append_sheet(wb, sheet([workHead, ...summary.map(sumRow)], [8, 10, 15, 12, 13, 16, 10, 10]), 'Summary');
  XLSX.utils.book_append_sheet(wb, sheet([workHead, ...summary.filter(g => !isRefund(g.financial_status)).map(sumRow)], [8, 10, 15, 12, 13, 16, 10, 10]), 'Order');
  XLSX.utils.book_append_sheet(wb, sheet([workHead, ...summary.filter(g => isRefund(g.financial_status)).map(sumRow)], [8, 10, 15, 12, 13, 16, 10, 10]), 'Refund');

  const lines = group(rows.filter(r => !isRefund(r.financial_status)), ['store', 'source', 'financial_status', 'article', 'style', 'price'], ['quantity', 'subtotal', 'total'])
    .map(g => ({ ...g, rate: g._first.rate, freight: g._first.freight }))
    .sort((a, b) => a.article.localeCompare(b.article) || a.style.localeCompare(b.style) || a.price - b.price);
  const sources = [...new Set(lines.filter(hasRate).map(l => l.source))].sort();
  const sections = [...sources.map(s => [`Source: ${s}`, lines.filter(l => hasRate(l) && l.source === s)]),
    ['No cost rate (not supplied / rate missing)', lines.filter(l => !hasRate(l))]];
  XLSX.utils.book_append_sheet(wb, cogsSheet(sections, false, 'shopify'), 'COGS');
  XLSX.utils.book_append_sheet(wb, cogsSheet(sections, true, 'shopify'), 'Supplier COGS');

  const sum = (f, pick) => r2(rows.filter(pick).reduce((t, r) => t + (Number(r[f]) || 0), 0));
  const cogs = r2(lines.reduce((t, l) => t + l.quantity * (Number(l.rate) || 0), 0));
  const freight = r2(lines.reduce((t, l) => t + l.quantity * (Number(l.freight) || 0), 0));
  const profit = [['Shopify profit', 'Amount'], [],
    ['Sales (orders not refunded, subtotal)', sum('subtotal', r => !isRefund(r.financial_status))],
    ['Sales (orders not refunded, total incl. shipping & tax)', sum('total', r => !isRefund(r.financial_status))],
    ['Refunded orders (subtotal)', -sum('subtotal', r => isRefund(r.financial_status))],
    ['Refunds recorded by Shopify', -sum('refunded', () => true)], [],
    ['COGS (orders x rate)', -cogs], ['Freight (orders x freight)', -freight], [],
    ['Profit (sales subtotal - COGS - freight)', { f: 'B3+B8+B9' }],
    [], ['Shopify payment fees are not included (they are not in the Shopify order export either).']];
  XLSX.utils.book_append_sheet(wb, sheet(profit, [56, 14]), 'Profit');
  XLSX.utils.book_append_sheet(wb, checkSheet(rows.map(r => ({ ...r, store: 'Shopify', type: isRefund(r.financial_status) ? 'Refund' : 'Order' }))), 'Check');
  return wb;
}

export function download(wb, filename) {
  XLSX.writeFile(wb, filename, { compression: true });
}
